/**
 * Reference Field Hooks
 * 
 * Provides hooks for resolving reference fields - similar to React-Admin's
 * ReferenceField component but using TanStack Query for caching.
 * 
 * These hooks are useful when you have a list of items that reference
 * other resources (e.g., notes that reference conversations, which in
 * turn reference assistants).
 * 
 * @example
 * ```typescript
 * // In a Notes list component
 * const notes = useNotes({ page: 1, perPage: 25 })
 * 
 * // Get unique conversation IDs from notes
 * const conversationIds = useMemo(() => 
 *   [...new Set(notes.data?.data.map(n => n.conversationId).filter(Boolean))],
 *   [notes.data]
 * )
 * 
 * // Resolve conversations to get assistant IDs
 * const { data: conversationMap } = useReferenceMany(
 *   'conversations',
 *   conversationIds,
 *   { select: (c) => ({ id: c.id, assistantId: c.assistant_id }) }
 * )
 * 
 * // Now you can look up: noteRow.conversationId -> assistantId
 * ```
 */

import { useQuery } from '@tanstack/react-query'
import { raClient } from '@/lib/raClient'
import { createQueryKeys } from '@/lib/queryKeys'

// ============================================================================
// Types
// ============================================================================

export interface UseReferenceManyOptions<T, R> {
  /** Transform each record before caching */
  select?: (record: T) => R
  /** Whether to enable the query */
  enabled?: boolean
  /** Stale time in ms */
  staleTime?: number
}

export interface ReferenceMap<R> {
  /** Get a referenced record by ID */
  get: (id: string | number) => R | undefined
  /** Check if we have data for an ID */
  has: (id: string | number) => boolean
  /** All resolved records */
  data: R[]
  /** Raw map for direct access */
  map: Map<string | number, R>
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch multiple records from a resource and return them as a lookup map
 * 
 * This is the primary hook for resolving reference fields. It fetches
 * records by IDs and returns them in a Map for O(1) lookups.
 * 
 * @example
 * ```typescript
 * // Basic usage
 * const { data: assistantMap } = useReferenceMany<Assistant>('assistants', [1, 2, 3])
 * const name = assistantMap?.get(1)?.name
 * 
 * // With selection (extract only needed fields)
 * const { data: assistantMap } = useReferenceMany<Assistant, { id: number; name: string }>(
 *   'assistants',
 *   assistantIds,
 *   { select: (a) => ({ id: a.id, name: a.name }) }
 * )
 * ```
 */
export function useReferenceMany<T extends { id: string | number }, R = T>(
  resource: string,
  ids: (string | number)[],
  options: UseReferenceManyOptions<T, R> = {}
): ReturnType<typeof useQuery<ReferenceMap<R>>> {
  const { select, enabled = true, staleTime = 5 * 60 * 1000 } = options
  const keys = createQueryKeys(resource)

  // Deduplicate and filter out falsy IDs
  const uniqueIds = [...new Set(ids.filter(Boolean))]

  return useQuery({
    queryKey: [...keys.byIds(uniqueIds), 'reference'],
    queryFn: async (): Promise<ReferenceMap<R>> => {
      if (!uniqueIds.length) {
        return {
          get: () => undefined,
          has: () => false,
          data: [],
          map: new Map(),
        }
      }

      const records = await raClient.getMany<T>(resource, uniqueIds)
      const transformed = select ? records.map(select) : (records as unknown as R[])

      // Build lookup map - use original ID for the key
      const map = new Map<string | number, R>()
      records.forEach((record, index) => {
        map.set(record.id, transformed[index])
      })

      return {
        get: (id) => map.get(id),
        has: (id) => map.has(id),
        data: transformed,
        map,
      }
    },
    enabled: enabled && uniqueIds.length > 0,
    staleTime,
  })
}

/**
 * Resolve a chain of references (e.g., note -> conversation -> assistant)
 * 
 * This hook is useful when you need to traverse multiple levels of references.
 * 
 * @example
 * ```typescript
 * // Get assistant names for a list of notes
 * const notes = useNotes({ page: 1 })
 * 
 * const { data: assistantMap } = useReferenceChain({
 *   // Source: extract conversation IDs from notes
 *   sourceIds: notes.data?.data.map(n => n.conversationId).filter(Boolean) ?? [],
 *   
 *   // Middle: fetch conversations to get assistant IDs
 *   middleResource: 'conversations',
 *   extractTargetId: (conversation) => conversation.assistant_id,
 *   
 *   // Target: fetch assistants
 *   targetResource: 'assistants',
 *   targetSelect: (assistant) => ({ id: assistant.id, name: assistant.name }),
 *   
 *   // Build the final map: sourceId -> targetData
 *   buildSourceMap: true,
 * })
 * 
 * // Now: assistantMap.getBySourceId(note.conversationId)?.name
 * ```
 */
export function useReferenceChain<
  M extends { id: string | number },
  T extends { id: string | number },
  R = T
>(options: {
  sourceIds: (string | number)[]
  middleResource: string
  extractTargetId: (middle: M) => string | number | undefined
  targetResource: string
  targetSelect?: (target: T) => R
  enabled?: boolean
}): {
  /** Map from source ID to target data */
  getBySourceId: (sourceId: string | number) => R | undefined
  /** Map from target ID to target data */
  getByTargetId: (targetId: string | number) => R | undefined
  /** All resolved target records */
  data: R[]
  /** Loading state */
  isLoading: boolean
  /** Whether any queries are still pending */
  isPending: boolean
} {
  const {
    sourceIds,
    middleResource,
    extractTargetId,
    targetResource,
    targetSelect,
    enabled = true,
  } = options

  // First, fetch the middle resources (e.g., conversations)
  const { data: middleMap, isLoading: middleLoading } = useReferenceMany<M>(
    middleResource,
    sourceIds,
    { enabled }
  )

  // Extract target IDs from middle resources
  const targetIds =
    middleMap?.data
      .map(extractTargetId)
      .filter((id): id is string | number => id != null) ?? []

  // Fetch target resources (e.g., assistants)
  const { data: targetMap, isLoading: targetLoading } = useReferenceMany<T, R>(
    targetResource,
    targetIds,
    {
      select: targetSelect,
      enabled: enabled && !middleLoading && targetIds.length > 0,
    }
  )

  // Build source -> target mapping
  const getBySourceId = (sourceId: string | number): R | undefined => {
    const middle = middleMap?.get(sourceId)
    if (!middle) return undefined
    const targetId = extractTargetId(middle)
    if (!targetId) return undefined
    return targetMap?.get(targetId)
  }

  return {
    getBySourceId,
    getByTargetId: (id) => targetMap?.get(id),
    data: targetMap?.data ?? [],
    isLoading: middleLoading || targetLoading,
    isPending: middleLoading || (targetIds.length > 0 && targetLoading),
  }
}
